import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  CircularProgress,
  Alert,
} from '@mui/material';
import api from '../../utils/api';
import UnifiedSupplierProfile from '../Supplier/UnifiedSupplierProfile';
import { supplierCategoryLabels } from '../../config/supplierTaxonomy';

function PublicBusinessProfile() {
  const { businessId: urlBusinessId, slug: urlSlug } = useParams();
  const navigate = useNavigate();
  
  // Check if we're being proxied from WordPress
  const isWordPressProxy = window.__WORDPRESS_PROXY__ === true;
  const wordPressBusinessId = window.__PROFILE_BUSINESS_ID__;
  const wordPressSlug = window.__PROFILE_SLUG__;
  
  // Use WordPress proxy values if available, otherwise use URL params
  const businessId = isWordPressProxy && wordPressBusinessId ? wordPressBusinessId : urlBusinessId;
  const slug = isWordPressProxy && wordPressSlug ? wordPressSlug : urlSlug;
  
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);
  const [_formLoading, setFormLoading] = useState(false);

  // Prevent React Router from changing URL when proxied from WordPress
  // Keep the clean WordPress URL instead of React Router's /profile/:businessId/:slug
  useEffect(() => {
    if (!isWordPressProxy || !window.__WORDPRESS_PATH__) return;
    
    const wordPressPath = window.__WORDPRESS_PATH__;
    
    // Restore the WordPress URL if it gets changed
    const checkAndRestore = () => {
      const currentPath = window.location.pathname;
      // If URL changed to /profile/... format, restore WordPress URL
      if (currentPath.startsWith('/profile/') && currentPath !== wordPressPath) {
        window.history.replaceState(null, '', wordPressPath + window.location.search + window.location.hash);
      }
    };
    
    // Check immediately and after a short delay (React Router might change it after initial render)
    checkAndRestore();
    const timeout = setTimeout(checkAndRestore, 100);
    const timeout2 = setTimeout(checkAndRestore, 500);
    
    return () => {
      clearTimeout(timeout);
      clearTimeout(timeout2);
    };
  }, [isWordPressProxy]);

  // Category labels
  const categoryLabels = supplierCategoryLabels;

  // Price range labels
  const _priceRangeLabels = {
    '£': 'Budget Friendly',
    '££': 'Mid Range',
    '£££': 'Premium',
    '££££': 'Luxury',
  };

  useEffect(() => {
    // Only proceed if we have a businessId
    if (!businessId) {
      setError('Business ID is required');
      setLoading(false);
      return;
    }

    // List of known supplier routes that shouldn't match business profiles
    const knownRoutes = [
      'dashboard', 'login', 'register', 'settings', 'bookings', 'messages', 
      'crm', 'forms', 'staff', 'weddings', 'edit-profile', 'lumi', 'chat',
      'notifications', 'billing', 'subscriptions', 'verification', 'profile',
      'availability', 'team-members', 'ai-chat-test'
    ];

    // Note: This check is no longer needed since we're using /profile route
    // But keeping it as a safety check
    if (knownRoutes.includes(businessId.toLowerCase())) {
      setError('Invalid business profile URL');
      setLoading(false);
      return;
    }

    // Validate businessId is a valid ObjectId format (24 hex characters)
    const objectIdRegex = /^[0-9a-fA-F]{24}$/;
    if (objectIdRegex.test(businessId)) {
      fetchBusinessProfile();
    } else {
      // Invalid format
      setError('Invalid business profile URL');
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId, navigate]);

  useEffect(() => {
    // Update SEO meta tags when business data loads
    if (business) {
      // Update document title
      const categoryLabel = categoryLabels[business.category] || business.category;
      document.title = `${business.name} - ${categoryLabel} | Idotoo`;

      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute(
        'content',
        business.description || `${business.name} - ${categoryLabel} wedding services`
      );

      // Update Open Graph tags
      const updateMetaTag = (property, content) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', property);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      updateMetaTag('og:title', `${business.name} - ${categoryLabel} | Idotoo`);
      updateMetaTag('og:description', business.description || '');
      updateMetaTag('og:type', 'business.business');
      updateMetaTag('og:url', window.location.href);
      if (business.media?.coverImage || business.media?.logo) {
        updateMetaTag('og:image', business.media.coverImage || business.media.logo);
      }

      // Add Schema.org JSON-LD
      addSchemaMarkup();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [business]);

  const addSchemaMarkup = () => {
    // Remove existing schema if any
    const existingSchema = document.querySelector('#business-schema');
    if (existingSchema) {
      existingSchema.remove();
    }

    if (!business) return;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: business.name,
      description: business.description,
      image: business.media?.coverImage || business.media?.logo,
      address: business.contactInfo?.address ? {
        '@type': 'PostalAddress',
        streetAddress: business.contactInfo.address.street,
        addressLocality: business.contactInfo.address.city,
        addressRegion: business.contactInfo.address.state,
        postalCode: business.contactInfo.address.postalCode,
        addressCountry: business.contactInfo.address.country,
      } : undefined,
      telephone: business.contactInfo?.primaryPhone,
      url: business.contactInfo?.website,
      priceRange: business.priceRange,
      ...(business.ratings?.count > 0 && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: business.ratings.average,
          reviewCount: business.ratings.count,
        },
      }),
      ...(business.openingHours && {
        openingHoursSpecification: Object.entries(business.openingHours)
          .filter(([_, hours]) => hours.isOpen)
          .map(([day, hours]) => ({
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: `https://schema.org/${day.charAt(0).toUpperCase() + day.slice(1)}`,
            opens: hours.openTime,
            closes: hours.closeTime,
          })),
      }),
    };

    const script = document.createElement('script');
    script.id = 'business-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
  };

  const fetchBusinessProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/api/public/business/${businessId}`);
      const data = response.data;

      // Verify slug matches (redirect if not)
      // BUT: Don't redirect if we're being proxied from WordPress - keep the clean URL
      if (!isWordPressProxy) {
        if (slug && data.slug && slug !== data.slug) {
          navigate(`/profile/${businessId}/${data.slug}`, { replace: true });
          return;
        }

        // If no slug in URL but business has slug, redirect
        if (!slug && data.slug) {
          navigate(`/profile/${businessId}/${data.slug}`, { replace: true });
          return;
        }
      }

      setBusiness(data);

      // Fetch form data if defaultFormId exists
      if (data.defaultFormId) {
        fetchFormData(data.defaultFormId);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        const reason = err.response?.data?.reason;
        if (reason) {
          setError(`Business not found. ${reason}`);
        } else {
          setError('Business not found. This business may not exist or may not be publicly available.');
        }
      } else {
        setError('Failed to load business profile. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFormData = async (formId) => {
    try {
      setFormLoading(true);
      const response = await api.get(`/api/public/forms/${formId}`);
      setFormData(response.data);
    } catch (err) {} finally {
      setFormLoading(false);
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!business) {
    return null;
  }

  // Transform business data to supplier format for unified component
  const supplierData = {
    ...business,
    businessName: business.name,
    coverImage: business.media?.coverImage,
    gallery: business.media?.gallery || [],
    imageUrl: business.media?.logo,
    address: business.contactInfo?.address,
    contactName: business.contactInfo?.contactName,
    phone: business.contactInfo?.primaryPhone,
    email: business.contactInfo?.email,
    website: business.contactInfo?.website,
    faqs: business.faq || [],
    policies: business.policies || {},
    venueDetails: business.venueDetails,
    openingHours: business.openingHours,
    teamMembers: business.teamMembers || [],
    recommendedSuppliers: business.recommendedSuppliers || [],
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header/Navigation */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2,
            }}
          >
            <Box
              component="a"
              href="https://idotoo.ai"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              <img
                src="https://app.idotoo.ai/img/logo.svg"
                alt="Idotoo Logo"
                style={{ height: '32px' }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <UnifiedSupplierProfile
        supplier={supplierData}
        loading={loading}
        error={error}
        isPublic={true}
        formData={formData}
        onEnquireClick={() => navigate(`/book-supplier/${businessId}`)}
      />
    </Box>
  );
}

export default PublicBusinessProfile;

